<template>
    <div class="body_costs_analysis">
        <div v-if="isLoading" class="state_message">
            <p>Cargando análisis…</p>
        </div>

        <div v-else-if="error" class="state_message">
            <p class="error_text">{{ error }}</p>
        </div>

        <div v-else-if="analysis" class="analysis_grid">
            <CostStatementCard class="cell"
                :period-label="analysis.period_label"
                :headline="analysis.headline"
                :statement="analysis.statement" />

            <CounterpartsRankingCard class="cell"
                v-model="counterpartsQueryModel"
                :counterparts="filteredCounterparts"
                @counterpart-click="openCounterpartDetail" />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import CostStatementCard from "./CostStatementCard.vue";
import CounterpartsRankingCard from "./CounterpartsRankingCard.vue";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight.vue";
import HeaderClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/HeaderClientDetail.vue";
import BodyClientDetail from "@/components/financialReports/salesAnalysis/clientDetail/BodyClientDetail.vue";

// El endpoint de metadata encuentra a la contraparte por su obligación. Los
// tipos "archivo" (Honorario/Trabajador/Internacional/Recibo/Finiquito) tienen
// cada uno su obligación; Proveedor/Cliente usan facturas/boletas normales →
// bucket "Otros". El ranking ya nos da el counterpart_type, así que pedimos la
// metadata con la obligación que le corresponde (no "Otros" genérico, que
// dejaría fuera a los tipos archivo, ni "all", que mezclaría tipos distintos).
const METADATA_OBLIGATION_BY_TYPE = {
    Honorario: "Boleta de Honorarios",
    Trabajador: "remuneracion-clf",
    Internacional: "invoices-clf",
    Recibo: "recibos-clf",
    Finiquito: "finiquitos-clf",
};

export default {
    name: "BodyCostsAnalysis",
    components: {
        CostStatementCard,
        CounterpartsRankingCard,
    },
    // Orquestador del feature: lee del módulo costsAnalysis y reparte slices a
    // las dos cards presentacionales. El modal pasa `bodyProps: null` — la
    // lectura es vía Vuex.
    computed: {
        ...mapState("costsAnalysis", {
            analysis: "data",
            isLoading: "isLoading",
            error: "error",
            analysisMonth: "month",
        }),
        ...mapGetters("costsAnalysis", ["filteredCounterparts"]),
        // Bridge entre el v-model del ranking y el store global. El padre asume
        // el acople a Vuex; la card se queda pura.
        counterpartsQueryModel: {
            get() { return this.$store.state.costsAnalysis.counterpartsQuery; },
            set(value) { this.setCounterpartsQuery(value); },
        },
    },
    methods: {
        ...mapMutations("costsAnalysis", { setCounterpartsQuery: "SET_COUNTERPARTS_QUERY" }),
        ...mapActions("clientDetail", {
            openClientDetailAction: "openClientDetail",
            closeClientDetailAction: "closeClientDetail",
            changeClientRangePeriod: "changeRangePeriod",
        }),

        // Drill-down de segundo nivel: reutiliza el drawer clientDetail (mismo
        // que Ventas) parametrizado para Costos. V1: todas las contrapartes de
        // costos se resuelven como Proveedor (document_type='P'); honorarios/
        // internacional/etc. son también lado-egreso. Si en el futuro hay que
        // distinguir por tipo, centralizar el mapeo acá (una sola línea).
        // Obligación con la que el endpoint de metadata encuentra a la
        // contraparte, según su tipo. Tipos no mapeados (Proveedor) → "Otros".
        obligationForType(counterpartType) {
            return METADATA_OBLIGATION_BY_TYPE[counterpartType] || "Otros";
        },

        openCounterpartDetail(counterpart) {
            if (!counterpart || !counterpart.name || !this.analysisMonth) return;

            const documentType = "P";

            this.openClientDetailAction({
                alias: counterpart.name,
                documentType,
                monthIso: this.analysisMonth,
                // Metadata pedida con la obligación del tipo clickeado, para
                // traer la contraparte correcta (Honorario/Trabajador/… quedan
                // fuera del bucket "Otros"; "all" mezclaría tipos distintos).
                obligationName: this.obligationForType(counterpart.counterpart_type),
                // Subject por level_1 ("Costos"): los level_2 de costos son
                // sub-tipos, filtrar por uno solo perdería el resto. El
                // denominador "% de Ventas" del chart sigue en level2Name.
                level1Name: "Costos",
                // Acota docs + serie al mismo tipo que muestra la card (el
                // ranking ya lo conoce), para que todo el modal sea coherente.
                counterpartType: counterpart.counterpart_type,
            });

            this.$buefy.modal.open({
                parent: this,
                hasModalCard: true,
                customClass: "wide-summary-modal",
                canCancel: ["escape", "outside"],
                component: ModalFullHeightRight,
                props: {
                    headerComp: HeaderClientDetail,
                    bodyComp: BodyClientDetail,
                    headerProps: { fallbackAlias: counterpart.name },
                    bodyProps: null,
                    // El theme del tab Costos se propaga al segundo drawer para
                    // que los tokens --report-* del subárbol respeten la paleta.
                    theme: "costos",
                    // Ancho menor que el primer drawer (85%) para que se perciba
                    // como un segundo nivel apilado encima del análisis del mes.
                    width: "70%",
                },
                events: {
                    closed: this.closeClientDetailAction,
                    "update-range-period": this.changeClientRangePeriod,
                },
            });
        },
    },
};
</script>

<style scoped lang="scss">
// Cadena de altura: el body absorbe lo que le da .modal_back_caa (flex:1) y lo
// propaga vía grid → cells → cards. El scroll vive DENTRO de cada card, nunca
// acá. `min-height: 0` en body+grid+cell respeta el constraint a través de los
// layers (mismo patrón que BodySalesAnalysis).
.body_costs_analysis {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.state_message {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    text-align: center;
    color: var(--report-text-muted);
    font-size: 14px;
}

.error_text {
    color: var(--report-negative);
}

// Layout 2 columnas (no el 2×2 de Ventas): estado de costos a la izquierda,
// ranking de contrapartes a la derecha. Ambas cards a altura completa; cada
// una scrollea internamente su lista/estado.
.analysis_grid {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
}

.cell {
    min-width: 0;
    min-height: 0;
}
</style>
